/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getLibrary,
    createOrUpdateLibrary,
    getInternetFacility,
    createOrUpdateInternetFacility,
    getTrainingReport,
    createOrUpdateTrainingReport,
    getGoodPractice,
    createOrUpdateGoodPractice,
    getConsultation,
    createOrUpdateConsultation,
    getProjectsPartnership,
    createOrUpdateProjectsPartnership,
    getAllSchoolsWithEducationCenterData,
    getExpertise,
    createOrUpdateExpertise,
    getLeadershipManagement,
    createOrUpdateLeadershipManagement,
    getPlanningAdministration,
    createOrUpdatePlanningAdministration,
    getProgramsEvaluation,
    createOrUpdateProgramsEvaluation,
} from './centerAccreditationApi';
import { logoutUser } from '../user/userApi';
import { CenterAccreditationState } from './types';

const initialState: CenterAccreditationState = {
    error: null,
    library: null,
    isLibraryFetching: false,
    internetFacility: null,
    isInternetFacilityFetching: false,
    trainingReport: null,
    isTrainingReportFetching: false,
    goodPractice: null,
    isGoodPracticeFetching: false,
    consultation: null,
    isConsultationFetching: false,
    projectsPartnership: null,
    isProjectsPartnershipFetching: false,
    educationCenterData: null,
    isEducationCenterDataFetching: false,
    expertise: null,
    isExpertiseFetching: false,
    leadershipManagement: null,
    isLeadershipManagementFetching: false,
    planningAdministration: null,
    isPlanningAdministrationFetching: false,
    programsEvaluation: null,
    isProgramsEvaluationFetching: false,
};

export const centerAccreditationSlice = createSlice({
    name: 'centerAccreditation',
    initialState,
    reducers: {
        resetLibrary: (state) => {
            state.library = null;
        },
        resetInternetFacility: (state) => {
            state.internetFacility = null;
            state.isInternetFacilityFetching = false;
        },
        resetTrainingReport: (state) => {
            state.trainingReport = null;
            state.isTrainingReportFetching = false;
        },
        resetGoodPractice: (state) => {
            state.goodPractice = null;
            state.isGoodPracticeFetching = false;
        },
        resetConsultation: (state) => {
            state.consultation = null;
            state.isConsultationFetching = false;
        },
        resetProjectsPartnership: (state) => {
            state.projectsPartnership = null;
            state.isProjectsPartnershipFetching = false;
        },
        resetExpertise: (state) => {
            state.expertise = null;
            state.isExpertiseFetching = false;
        },
        resetLeadershipManagement: (state) => {
            state.leadershipManagement = null;
            state.isLeadershipManagementFetching = false;
        },
        resetPlanningAdministration: (state) => {
            state.planningAdministration = null;
            state.isPlanningAdministrationFetching = false;
        },
        resetProgramsEvaluation: (state) => {
            state.programsEvaluation = null;
            state.isProgramsEvaluationFetching = false;
        },
    },
    extraReducers: (builder) => {
        // Get library
        builder
            .addCase(getLibrary.fulfilled, (state: CenterAccreditationState, action) => {
                state.isLibraryFetching = false;
                state.library = action?.payload?.library || null;
            });

        // Create or update library
        builder
            .addCase(createOrUpdateLibrary.fulfilled, (state: CenterAccreditationState, action) => {
                state.isLibraryFetching = false;
                state.library = action?.payload?.library || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Bibliotekos duomenys sėkmingai išsaugoti',
                });
            });

        // Get internet facility
        builder
            .addCase(getInternetFacility.fulfilled, (state: CenterAccreditationState, action) => {
                state.isInternetFacilityFetching = false;
                state.internetFacility = action?.payload?.facility || null;
            });

        // Create or update internet facility
        builder
            .addCase(createOrUpdateInternetFacility.fulfilled, (state: CenterAccreditationState, action) => {
                state.isInternetFacilityFetching = false;
                state.internetFacility = action?.payload?.facility || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Interneto įrenginių duomenys sėkmingai išsaugoti',
                });
            });

        // Get training report
        builder
            .addCase(getTrainingReport.fulfilled, (state: CenterAccreditationState, action) => {
                state.isTrainingReportFetching = false;
                state.trainingReport = action?.payload?.report || null;
            });

        // Create or update training report
        builder
            .addCase(createOrUpdateTrainingReport.fulfilled, (state: CenterAccreditationState, action) => {
                state.isTrainingReportFetching = false;
                state.trainingReport = action?.payload?.report || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Mokymo ataskaitos duomenys sėkmingai išsaugoti',
                });
            });

        // Get good practice
        builder
            .addCase(getGoodPractice.fulfilled, (state: CenterAccreditationState, action) => {
                state.isGoodPracticeFetching = false;
                state.goodPractice = action?.payload?.goodPractice || null;
            });

        // Create or update good practice
        builder
            .addCase(createOrUpdateGoodPractice.fulfilled, (state: CenterAccreditationState, action) => {
                state.isGoodPracticeFetching = false;
                state.goodPractice = action?.payload?.goodPractice || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Gerųjų praktikų duomenys sėkmingai išsaugoti',
                });
            });

        // Get consultation
        builder
            .addCase(getConsultation.fulfilled, (state: CenterAccreditationState, action) => {
                state.isConsultationFetching = false;
                state.consultation = action?.payload?.consultation || null;
            });

        // Create or update consultation
        builder
            .addCase(createOrUpdateConsultation.fulfilled, (state: CenterAccreditationState, action) => {
                state.isConsultationFetching = false;
                state.consultation = action?.payload?.consultation || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Konsultavimo duomenys sėkmingai išsaugoti',
                });
            });

        // Get projects partnership
        builder
            .addCase(getProjectsPartnership.fulfilled, (state: CenterAccreditationState, action) => {
                state.isProjectsPartnershipFetching = false;
                state.projectsPartnership = action?.payload?.projectsPartnership || null;
            });

        // Create or update projects partnership
        builder
            .addCase(createOrUpdateProjectsPartnership.fulfilled, (state: CenterAccreditationState, action) => {
                state.isProjectsPartnershipFetching = false;
                state.projectsPartnership = action?.payload?.projectsPartnership || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Projektų ir partnerysčių duomenys sėkmingai išsaugoti',
                });
            });

        // Get all schools with education center data
        builder.addCase(getAllSchoolsWithEducationCenterData.fulfilled, (state: CenterAccreditationState, action) => {
            state.isEducationCenterDataFetching = false;
            state.educationCenterData = action?.payload;
        });

        // Get expertise
        builder
            .addCase(getExpertise.fulfilled, (state: CenterAccreditationState, action) => {
                state.isExpertiseFetching = false;
                state.expertise = action?.payload?.expertise || null;
            });

        // Create or update expertise
        builder
            .addCase(createOrUpdateExpertise.fulfilled, (state: CenterAccreditationState, action) => {
                state.isExpertiseFetching = false;
                state.expertise = action?.payload?.expertise || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Ekspertizės duomenys sėkmingai išsaugoti',
                });
            });

        // Get leadership management
        builder
            .addCase(getLeadershipManagement.fulfilled, (state: CenterAccreditationState, action) => {
                state.isLeadershipManagementFetching = false;
                state.leadershipManagement = action?.payload?.leadershipManagement || null;
            });

        // Create or update leadership management
        builder
            .addCase(createOrUpdateLeadershipManagement.fulfilled, (state: CenterAccreditationState, action) => {
                state.isLeadershipManagementFetching = false;
                state.leadershipManagement = action?.payload?.leadershipManagement || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Vadovavimo ir valdymo duomenys sėkmingai išsaugoti',
                });
            });

        // Get planning administration
        builder
            .addCase(getPlanningAdministration.fulfilled, (state: CenterAccreditationState, action) => {
                state.isPlanningAdministrationFetching = false;
                state.planningAdministration = action?.payload?.planningAdministration || null;
            });

        // Create or update planning administration
        builder
            .addCase(createOrUpdatePlanningAdministration.fulfilled, (state: CenterAccreditationState, action) => {
                state.isPlanningAdministrationFetching = false;
                state.planningAdministration = action?.payload?.planningAdministration || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Planavimo ir administravimo duomenys sėkmingai išsaugoti',
                });
            });

        // Get programs evaluation
        builder
            .addCase(getProgramsEvaluation.fulfilled, (state: CenterAccreditationState, action) => {
                state.isProgramsEvaluationFetching = false;
                state.programsEvaluation = action?.payload?.programsEvaluation || null;
            });

        // Create or update programs evaluation
        builder
            .addCase(createOrUpdateProgramsEvaluation.fulfilled, (state: CenterAccreditationState, action) => {
                state.isProgramsEvaluationFetching = false;
                state.programsEvaluation = action?.payload?.programsEvaluation || null;

                notification.success({
                    message: 'Pavyko!',
                    description: 'Programų vertinimo duomenys sėkmingai išsaugoti',
                });
            });

        // On logout
        builder
            .addCase(logoutUser.fulfilled, (state: CenterAccreditationState) => {
                state.library = null;
                state.internetFacility = null;
                state.trainingReport = null;
                state.goodPractice = null;
                state.consultation = null;
                state.educationCenterData = null;
                state.expertise = null;
                state.leadershipManagement = null;
                state.planningAdministration = null;
                state.programsEvaluation = null;
            });

        // Library pending state
        builder.addMatcher(
            isAnyOf(
                getLibrary.pending,
                createOrUpdateLibrary.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isLibraryFetching = true;
                state.error = null;
            },
        );

        // Internet facility pending state
        builder.addMatcher(
            isAnyOf(
                getInternetFacility.pending,
                createOrUpdateInternetFacility.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isInternetFacilityFetching = true;
                state.error = null;
            },
        );

        // Training report pending state
        builder.addMatcher(
            isAnyOf(
                getTrainingReport.pending,
                createOrUpdateTrainingReport.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isTrainingReportFetching = true;
                state.error = null;
            },
        );

        // Good practice pending state
        builder.addMatcher(
            isAnyOf(
                getGoodPractice.pending,
                createOrUpdateGoodPractice.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isGoodPracticeFetching = true;
                state.error = null;
            },
        );

        // Consultation pending state
        builder.addMatcher(
            isAnyOf(
                getConsultation.pending,
                createOrUpdateConsultation.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isConsultationFetching = true;
                state.error = null;
            },
        );

        // Projects partnership pending state
        builder.addMatcher(
            isAnyOf(
                getProjectsPartnership.pending,
                createOrUpdateProjectsPartnership.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isProjectsPartnershipFetching = true;
                state.error = null;
            },
        );

        // Education center data pending state
        builder.addMatcher(
            isAnyOf(
                getAllSchoolsWithEducationCenterData.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isEducationCenterDataFetching = true;
                state.error = null;
            },
        );

        // Expertise pending state
        builder.addMatcher(
            isAnyOf(
                getExpertise.pending,
                createOrUpdateExpertise.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isExpertiseFetching = true;
                state.error = null;
            },
        );

        // Leadership management pending state
        builder.addMatcher(
            isAnyOf(
                getLeadershipManagement.pending,
                createOrUpdateLeadershipManagement.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isLeadershipManagementFetching = true;
                state.error = null;
            },
        );

        // Planning administration pending state
        builder.addMatcher(
            isAnyOf(
                getPlanningAdministration.pending,
                createOrUpdatePlanningAdministration.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isPlanningAdministrationFetching = true;
                state.error = null;
            },
        );

        // Programs evaluation pending state
        builder.addMatcher(
            isAnyOf(
                getProgramsEvaluation.pending,
                createOrUpdateProgramsEvaluation.pending,
            ),
            (state: CenterAccreditationState) => {
                state.isProgramsEvaluationFetching = true;
                state.error = null;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                getLibrary.rejected,
                createOrUpdateLibrary.rejected,
                getInternetFacility.rejected,
                createOrUpdateInternetFacility.rejected,
                getTrainingReport.rejected,
                createOrUpdateTrainingReport.rejected,
                getGoodPractice.rejected,
                createOrUpdateGoodPractice.rejected,
                getConsultation.rejected,
                createOrUpdateConsultation.rejected,
                getProjectsPartnership.rejected,
                createOrUpdateProjectsPartnership.rejected,
                getAllSchoolsWithEducationCenterData.rejected,
                getExpertise.rejected,
                createOrUpdateExpertise.rejected,
                getLeadershipManagement.rejected,
                createOrUpdateLeadershipManagement.rejected,
                getPlanningAdministration.rejected,
                createOrUpdatePlanningAdministration.rejected,
                getProgramsEvaluation.rejected,
                createOrUpdateProgramsEvaluation.rejected,
            ),
            (state: CenterAccreditationState, action) => {
                state.isLibraryFetching = false;
                state.isInternetFacilityFetching = false;
                state.isTrainingReportFetching = false;
                state.isGoodPracticeFetching = false;
                state.isConsultationFetching = false;
                state.isProjectsPartnershipFetching = false;
                state.isEducationCenterDataFetching = false;
                state.isExpertiseFetching = false;
                state.isLeadershipManagementFetching = false;
                state.isPlanningAdministrationFetching = false;
                state.isProgramsEvaluationFetching = false;
                state.error = action.error;
            },
        );
    },
});

export const { resetLibrary, resetInternetFacility, resetTrainingReport, resetGoodPractice, resetConsultation, resetProjectsPartnership, resetExpertise, resetLeadershipManagement, resetPlanningAdministration, resetProgramsEvaluation } = centerAccreditationSlice.actions;
export default centerAccreditationSlice.reducer;
